import { useState, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";

import {
  ToastProvider,
  BottomNavigationBar,
  SidebarContent,
  SidebarContentProps,
  NavItem,
  SIDEBAR_GRID_EXPANDED,
  SIDEBAR_GRID_COLLAPSED,
  cn,
} from "@racoon/ui";
import {
  IcDashboard,
  IcChat,
  IcBunker,
  IcAnalytics,
  IcProfile,
  IcGroupOfLocations,
  IcNavigation,
  LogoSentinel,
} from "@racoon/icons";
import { useFcmToken } from "~/shared/hooks/useFcmToken";
import { useCurrentUser } from "~/shared/hooks/useCurrentUser";
import { useCanEditGovDash } from "~/shared/hooks/useCanEditGovDash";
import { useSidebarCollapsed } from "~/shared/store/ui";
import { Namespace, useT } from "@racoon/i18n";

export default function PrivateLayout() {
  useFcmToken();
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const settingsMenuRef = useRef<HTMLDivElement | null>(null);
  const { pathname } = useLocation();
  const currentUser = useCurrentUser();
  const canEditGovDash = useCanEditGovDash();
  const { isSidebarCollapsed, setCollapsed } = useSidebarCollapsed();

  const t = useT();

  const sidebarUser = currentUser
    ? {
        first_name: currentUser.firstName || "",
        last_name: currentUser.lastName || "",
        role: currentUser.role?.toString() || "",
      }
    : null;

  // Mobile navigation items - simplified view
  const mobileNavItems: NavItem[] = [
    {
      to: "/dashboard",
      key: "dashboard",
      icon: IcDashboard,
      label: t(Namespace.Sidebar, "dashboard"),
    },
    {
      to: "/records",
      key: "records",
      icon: IcBunker,
      label: t(Namespace.Sidebar, "records"),
    },
    {
      to: "/chat",
      key: "chat",
      icon: IcChat,
      label: t(Namespace.Sidebar, "chat"),
    },
    {
      to: "/analytics",
      key: "analytics",
      icon: IcAnalytics,
      label: t(Namespace.Sidebar, "analytics"),
    },
    {
      to: "/profile",
      key: "profile",
      icon: IcProfile,
      label: t(Namespace.Sidebar, "profile"),
    },
  ];

  // Desktop sidebar items - detailed registries view
  const desktopNavItems: NavItem[] = [
    {
      to: "/dashboard",
      key: "dashboard",
      icon: IcGroupOfLocations,
      label: t(Namespace.Sidebar, "map"),
    },
    {
      to: "/monitor",
      key: "monitor",
      icon: IcDashboard,
      label: t(Namespace.Sidebar, "monitor"),
    },
    {
      to: "/records",
      key: "records",
      icon: IcBunker,
      label: t(Namespace.Sidebar, "records"),
    },
    // Desktop only, like the screen itself: the bottom bar does not list it. Editors only.
    ...(canEditGovDash
      ? [
          {
            to: "/gps-tracking",
            key: "gps-tracking",
            icon: IcNavigation,
            label: t(Namespace.Sidebar, "gpsTracking"),
          },
        ]
      : []),
    {
      to: "/chat",
      key: "chat",
      icon: IcChat,
      label: t(Namespace.Sidebar, "chat"),
    },
    {
      to: "/analytics",
      key: "analytics",
      icon: IcAnalytics,
      label: t(Namespace.Sidebar, "analytics"),
    },
  ];

  const activeNavItem =
    mobileNavItems.find((item) => pathname.includes(item.key))?.key ||
    desktopNavItems.find((item) => pathname.includes(item.key))?.key ||
    "dashboard";

  const sidebarItems: SidebarContentProps["sidebarItems"] = [
    {
      routes: desktopNavItems,
      title: t(Namespace.Sidebar, "groups.dispatcher"),
      icon: undefined,
      defaultOpen: true,
    },
  ];

  useEffect(() => {
    if (!showSettingsModal) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!settingsMenuRef.current) return;
      if (!settingsMenuRef.current.contains(event.target as Node)) {
        setShowSettingsModal(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowSettingsModal(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showSettingsModal]);

  return (
    <ToastProvider>
      <div className="flex flex-col h-dvh overflow-hidden bg-text-invert dark:bg-slate-950">
        <div className="lg:px-4 flex-1 min-h-0 overflow-hidden">
          <div
            className={cn(
              "h-full lg:grid lg:gap-4 lg:py-4 transition-[grid-template-columns] duration-300 ease-in-out",
              isSidebarCollapsed ? SIDEBAR_GRID_COLLAPSED : SIDEBAR_GRID_EXPANDED,
            )}
          >
            <aside className="hidden lg:block h-full">
              <div className="sticky top-0 bg-white dark:bg-slate-900 rounded-xl border overflow-hidden">
                <div className="h-[calc(100dvh-2rem)]">
                  <SidebarContent
                    sidebarItems={sidebarItems}
                    title={""}
                    user={sidebarUser}
                    logo={<LogoSentinel className="h-12 w-auto" />}
                    collapsedLogo={<LogoSentinel viewBox="0 0 48 48" className="h-9 w-9" />}
                    isCollapsed={isSidebarCollapsed}
                    onCollapsedChange={setCollapsed}
                  />
                </div>
              </div>
            </aside>

            <main className="min-w-0 h-full flex flex-col overflow-y-auto">
              <div className="flex flex-1 w-full min-h-0 relative">
                <Outlet />
              </div>
            </main>
          </div>
        </div>

        {/* Mobile Navigation Bar */}
        <BottomNavigationBar
          items={mobileNavItems}
          activeId={activeNavItem}
          className="lg:hidden shrink-0 px-4 border-t py-2 border-border bg-white dark:bg-slate-900"
        />
      </div>
    </ToastProvider>
  );
}
